import {
  Box,
  BoxProps,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Stack,
} from "@mui/material";
import { ReactNode, useRef } from "react";

import { ValidationErrors } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";

import ContactCard, { ContactCardData } from "./ContactCard";

export interface ContactSelectOption<TOptionData> extends ContactCardData {
  id: string;
  data?: TOptionData;
}

export interface ContactSelectProps<TOptionData> {
  options: ContactSelectOption<TOptionData>[];
  /** contact id */
  value?: string | null;
  errors?: ValidationErrors<any>;
  isAutoSelectSingleOption?: boolean;
  onChange?: (newValue: ContactSelectOption<TOptionData> | null) => void;
  renderContact?: (option: ContactSelectOption<TOptionData>) => ReactNode;
  sx?: BoxProps["sx"];
}

export default function ContactSelect<TOptionData>({
  options,
  value,
  errors,
  isAutoSelectSingleOption,
  onChange,
  renderContact,
  sx,
}: ContactSelectProps<TOptionData>) {
  const isAutoSelectedSingleOptionRef = useRef(false);
  if (isAutoSelectSingleOption && !isAutoSelectedSingleOptionRef.current && options.length === 1) {
    onChange?.(options[0]);
    isAutoSelectedSingleOptionRef.current = true;
    value = options[0].id; // for instant visual feedback
  }

  return (
    <Box sx={sx}>
      <Stack direction='column' spacing={1}>
        {options.map((option, index) => (
          <FormGroup key={index}>
            <FormControlLabel
              sx={{ margin: 0 }}
              disableTypography
              label={
                <>
                  {renderContact && renderContact(option)}
                  {!renderContact && (
                    <ContactCard contact={option} sx={{ width: "100%", minWidth: 300 }} />
                  )}
                </>
              }
              control={
                <Checkbox
                  checked={!!value && option.id === value}
                  onChange={(e) => {
                    onChange?.(e.target.checked ? option : null);
                  }}
                />
              }
            />
          </FormGroup>
        ))}
      </Stack>
      <FormHelperText error={Boolean(errors?.contactIds)}>
        {errors && ValidationHelper.getErrorsAsString(errors)}
      </FormHelperText>
    </Box>
  );
}
